import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaRobot, FaTimes, FaPaperPlane, FaUser } from 'react-icons/fa';

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: 1, text: "Hi there! I'm Nexcess AI assistant. How can I help you today?", sender: 'bot', timestamp: new Date() },
  ]);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    const userMessage = { id: messages.length + 1, text: inputValue, sender: 'user', timestamp: new Date() };
    setMessages([...messages, userMessage]);
    setInputValue('');
    setTimeout(() => {
      const botResponses = [
        "I'd be happy to help you with that!",
        'Thanks for reaching out. Let me check that for you.',
        'Great question! Our team specializes in that area.',
        'I can definitely assist with your project needs.',
        'Would you like to schedule a call with our team to discuss this further?',
      ];
      const random = botResponses[Math.floor(Math.random() * botResponses.length)];
      setMessages((prev) => [...prev, { id: prev.length + 2, text: random, sender: 'bot', timestamp: new Date() }]);
    }, 1000);
  };

  return (
    <>
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 left-6 z-40 w-14 h-14 flex items-center justify-center rounded-full text-white shadow-lg hover:shadow-2xl transition-shadow"
        style={{ background: 'linear-gradient(135deg, #0984e3 0%, #00b0ff 60%, #d500f9 100%)' }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
      >
        {isOpen ? <FaTimes size={20} /> : <FaRobot size={22} />}
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed bottom-24 left-6 w-[340px] md:w-96 bg-white border border-hairline rounded-2xl z-40 overflow-hidden flex flex-col shadow-soft-lg"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="p-4 border-b border-hairline flex justify-between items-center text-white" style={{ background: 'linear-gradient(135deg, #0984e3 0%, #00b0ff 60%, #d500f9 100%)' }}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <FaRobot size={16} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider opacity-80 font-semibold">Nexcess AI</div>
                  <h3 className="font-semibold text-base">Assistant</h3>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white" aria-label="Close chat">
                <FaTimes size={16} />
              </button>
            </div>

            <div className="h-80 overflow-y-auto p-4 space-y-3 bg-paper-2">
              {messages.map((message) => (
                <div key={message.id} className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[80%] p-3 rounded-2xl shadow-soft ${
                      message.sender === 'user'
                        ? 'bg-gradient-to-br from-nexcess-blue to-nexcess-cyan text-white rounded-br-sm'
                        : 'bg-white text-ink border border-hairline rounded-bl-sm'
                    }`}
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      {message.sender === 'user' ? <FaUser size={9} /> : <FaRobot size={10} />}
                      <span className="text-[10px] uppercase tracking-wider font-semibold opacity-80">
                        {message.sender === 'user' ? 'You' : 'Nexcess'}
                      </span>
                    </div>
                    <p className="text-sm leading-relaxed">{message.text}</p>
                    <div className="text-[10px] opacity-60 mt-1 text-right">
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            <form onSubmit={handleSendMessage} className="p-3 border-t border-hairline flex gap-2 bg-white">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Type your message..."
                className="input-field text-sm flex-grow"
              />
              <button
                type="submit"
                disabled={!inputValue.trim()}
                className="px-4 rounded-xl text-white disabled:opacity-40 transition-all hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg, #0984e3 0%, #00b0ff 60%, #d500f9 100%)' }}
                aria-label="Send"
              >
                <FaPaperPlane size={14} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatBot;
